import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { StatusMessage, mapApiError } from "../../components/ui/StatusMessage";
import {
  ArrowRight,
  Award,
  BookOpen,
  Brain,
  Calculator,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  FileText,
  GraduationCap,
  List,
  Loader2,
  Lock,
  Play,
  Search,
  Sparkles,
  Trophy,
  X,
  ArrowUpRight,
} from "lucide-react";
import { ListRow } from "../../components/ui/ListRow";
import { ActionPill } from "../../components/ui/ActionPill";
import { Skeleton } from "../../components/ui/Skeleton";
import { SubjectBadge } from "../../components/ui/Badges";
import { GlassIcons } from "../../components/ui/GlassIcons";
import { StatusBanner } from "../../components/ui/StatusBanner";
import { Backdrop } from "../../components/ui/Backdrop";
import { Modal } from "../../components/ui/Modal";
import { StudyBreadcrumb } from "../../components/study/StudyBreadcrumb";
import { ChaptersEntryCard } from "../../components/study/ChaptersEntryCard";
import { GeneratedContent } from "../../components/GeneratedContent";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { EmptyStateCard } from "../../components/ui/EmptyStateCard";
import { StudentChapterReaderModal } from "../../components/student/StudentChapterReaderModal";
import {
  ShortNotesReader,
  ShortNotesReaderSkeleton,
  ShortNotesReaderError,
  ShortNotesReaderEmpty,
} from "../../components/student/ShortNotesReader";
import { useAuth } from "../../context/AuthContext";
import { useStudent } from "../../context/StudentContext";
import { api, ApiError, type ModuleCurriculum, type StudentExam, type StudentResource, type TopicNoteReaderResponse } from "../../lib/api";
import { formatDisplayTitle } from "../../lib/formatTitle";
import { ONLINE_READING_MESSAGE, CONTENT_NOT_AVAILABLE_MESSAGE } from "../../lib/constants";
import { saveBlobAsFile, safeDownloadName } from "../../lib/download";
import { examContentToQuiz, getCachedSession, saveCachedSession, saveQuizSession } from "../../lib/quizSession";
import {
  STUDY_ACTIVITIES,
  findStudySubject,
  slugFromSubject,
} from "../../lib/studySubjects";
import { useStudySubjects } from "../../lib/useStudySubjects";

const ACTIVITY_ICONS = {
  book: BookOpen,
  brain: Brain,
  trophy: Trophy,
  sparkles: Sparkles,
};

const ACTIVITY_COLORS: Record<string, string> = {
  modules: "blue",
  quiz: "purple",
  exams: "orange",
  notes: "indigo",
};

function useResolvedStudySubject() {
  const { subject: subjectSlug } = useParams<{ subject: string }>();
  const { subjects, loading, error } = useStudySubjects();
  const resolved = useMemo(
    () => (subjectSlug ? findStudySubject(subjects, subjectSlug) : null),
    [subjects, subjectSlug],
  );
  return { subjectSlug, resolved, loading, error, subjects };
}

function StudySubjectLoading() {
  return (
    <div className="py-24 flex justify-center text-muted-foreground">
      <Loader2 className="animate-spin mr-2" /> Loading courses…
    </div>
  );
}

function getRecentSearches(): string[] {
  try {
    const raw = localStorage.getItem("soorgaa_recent_searches");
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function addRecentSearch(query: string) {
  if (!query.trim()) return;
  try {
    const existing = getRecentSearches();
    const updated = [
      query.trim(),
      ...existing.filter((s) => s.toLowerCase() !== query.trim().toLowerCase()),
    ].slice(0, 6);
    localStorage.setItem("soorgaa_recent_searches", JSON.stringify(updated));
  } catch {}
}

function clearRecentSearches() {
  try {
    localStorage.removeItem("soorgaa_recent_searches");
  } catch {}
}

/* ─── Global search results ─── */
export function StudyGlobalSearch() {
  const [params] = useSearchParams();
  const q = (params.get("search") ?? "").trim();
  const navigate = useNavigate();
  const { tokens } = useAuth();
  const { openUpgrade } = useStudent();
  const token = tokens?.accessToken ?? null;
  const [modules, setModules] = useState<StudentResource[]>([]);
  const [exams, setExams] = useState<StudentExam[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchError, setSearchError] = useState<unknown | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [recentList, setRecentList] = useState<string[]>(() => getRecentSearches());

  useEffect(() => {
    if (!token || !q) {
      setLoading(false);
      return;
    }
    addRecentSearch(q);
    setRecentList(getRecentSearches());
    setLoading(true);
    setSearchError(null);
    Promise.all([
      api.student.modules.list(token, { search: q }),
      api.student.exams.list(token),
    ])
      .then(([mods, allExams]) => {
        setModules(mods.items);
        const lower = q.toLowerCase();
        setExams(allExams.filter((e) => e.title.toLowerCase().includes(lower)));
      })
      .catch((err) => {
        setModules([]);
        setExams([]);
        setSearchError(err);
      })
      .finally(() => setLoading(false));
  }, [token, q]);

  if (!q) return <StudySubjectPicker />;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <Link to="/student/study" className="text-sm text-muted-foreground hover:text-primary">
            ← Study Hub
          </Link>
          <h2 className="text-2xl font-bold mt-1">Results for “{q}”</h2>
        </div>
      </div>

      {recentList.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <span className="text-muted-foreground font-semibold">Recent searches:</span>
          {recentList.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => navigate(`/student/study?search=${encodeURIComponent(item)}`)}
              className="px-2.5 py-1 rounded-md bg-muted text-foreground hover:bg-primary/10 hover:text-primary transition font-medium"
            >
              {item}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              clearRecentSearches();
              setRecentList([]);
            }}
            className="text-xs text-muted-foreground hover:text-destructive transition ml-1"
          >
            Clear
          </button>
        </div>
      )}

      {loading ? (
        <div className="py-16 flex justify-center">
          <Loader2 className="animate-spin" />
        </div>
      ) : searchError ? (
        <StatusMessage
          variant="error"
          title="Search failed"
          message={mapApiError(searchError).message}
          action={{ label: "Retry", onClick: () => navigate(`/student/study?search=${encodeURIComponent(q)}`) }}
        />
      ) : modules.length === 0 && exams.length === 0 ? (
        <EmptyStateCard
          title="No study materials found"
          description={`No modules, notes, or past exams match your search for "${q}".`}
          action={{
            label: "Reset search",
            onClick: () => navigate("/student/study"),
          }}
        />
      ) : (
        <>
          <section>
            <h3 className="font-semibold mb-3">Modules ({modules.length})</h3>
            {modules.length === 0 ? (
              <p className="text-sm text-muted-foreground">No modules match.</p>
            ) : (
              <div className="space-y-2">
                {modules.map((m) => {
                  return (
                    <div
                      key={m.id}
                      className="w-full text-left p-4 rounded-md border border-border bg-card hover:border-primary/50 transition flex flex-col justify-between"
                    >
                      <div>
                        <SubjectBadge subject={m.subject} />
                        <p className="font-semibold mt-1">{formatDisplayTitle(m.title)}</p>
                        {m.contentPublished ? (
                          <div className="flex items-center gap-2 mt-2">
                            <Link
                              to={`/student/study/${slugFromSubject(m.subject)}/quiz?moduleId=${m.id}`}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                            >
                              <Brain size={12} /> Quiz
                            </Link>
                            <span className="text-muted-foreground text-xs">•</span>
                            <Link
                              to={`/student/study/${slugFromSubject(m.subject)}/notes?moduleId=${m.id}`}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                            >
                              <FileText size={12} /> Notes
                            </Link>
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground italic mt-1.5">
                            {CONTENT_NOT_AVAILABLE_MESSAGE}
                          </p>
                        )}
                      </div>
                      <div className="mt-3 pt-2 border-t border-border flex items-center justify-between">
                        <button
                          type="button"
                          disabled={downloadingId === m.id}
                          onClick={async () => {
                            if (m.locked) {
                              openUpgrade();
                              return;
                            }
                            if (!token) {
                              navigate("/auth/login");
                              return;
                            }
                            setDownloadingId(m.id);
                            try {
                              const blob = await api.student.modules.downloadBlob(token, m.id);
                              saveBlobAsFile(blob, safeDownloadName(m.title, "module"));
                            } catch (err) {
                              setSearchError(err);
                            } finally {
                              setDownloadingId(null);
                            }
                          }}
                          className="text-xs text-primary font-semibold inline-flex items-center gap-1 hover:underline disabled:opacity-50"
                        >
                          <Download size={12} /> {downloadingId === m.id ? "Downloading..." : "Download PDF"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
          <section>
            <h3 className="font-semibold mb-3">Past exams ({exams.length})</h3>
            {exams.length === 0 ? (
              <p className="text-sm text-muted-foreground">No exams match.</p>
            ) : (
              <div className="space-y-2">
                {exams.map((e) => {
                  const slug = slugFromSubject(e.subject ?? "General");
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => navigate(`/student/study/${slug}/exams/${e.id}`)}
                      className="w-full text-left p-4 rounded-md border border-border bg-card hover:border-primary/50 transition"
                    >
                      <p className="font-semibold">{formatDisplayTitle(e.title)}</p>
                      <p className="text-xs text-muted-foreground">
                        {e.year} · {e.questions} questions
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

/* ─── Step 1: Pick subject ─── */
export function StudySubjectPicker() {
  const [params] = useSearchParams();
  const search = params.get("search");
  if (search?.trim()) return <StudyGlobalSearch />;

  const navigate = useNavigate();
  const { tokens } = useAuth();
  const token = tokens?.accessToken;
  const { openUpgrade, semesterStatuses, getSemesterStatus, isAdminPreview, refreshSemesterStatus } = useStudent();
  const { subjects, loading: subjectsLoading } = useStudySubjects();
  const [profileSemester, setProfileSemester] = useState<number>(1);
  // Cards render only when semester, plan and profile data are all known, so badges never jump.
  const [hubReady, setHubReady] = useState(false);

  useEffect(() => {
    if (!token) return;
    Promise.all([
      api.profile(token).then((p) => {
        if (p?.currentSemester) setProfileSemester(Number(p.currentSemester));
      }).catch(() => {}),
      refreshSemesterStatus().catch(() => []),
    ]).finally(() => setHubReady(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Dynamically compute semesters to display: always includes Semester 1 and 2, plus all semesters that are fully premium or have catalog courses/packages
  const semesterCards = useMemo(() => {
    const semsSet = new Set<number>([1, 2]);
    for (const st of semesterStatuses) {
      if (st.semester && st.semester > 0) semsSet.add(st.semester);
    }
    for (const sub of subjects) {
      if (sub.semester && sub.semester > 0) semsSet.add(sub.semester);
    }
    const sorted = Array.from(semsSet).sort((a, b) => a - b);

    return sorted.map((num) => {
      const courses = subjects.filter((s) => (s.semester ?? 1) === num);
      const status = getSemesterStatus(num) || semesterStatuses.find((s) => s.semester === num);
      const isFullyPremium = Boolean(status?.is_fully_premium || status?.isFullyPremium || num >= 2);

      return {
        num,
        title: `Semester ${num}`,
        badge: num === 1 ? "Freshman Year" : num === 2 ? "Stream Focus" : "Premium Curriculum",
        subtitle:
          num === 1
            ? "Freshman Semester I Curriculum"
            : num === 2
            ? "Freshman Semester II Curriculum"
            : `Advanced Semester ${num} Curriculum`,
        courses,
        status,
        isFullyPremium,
      };
    });
  }, [subjects, semesterStatuses, getSemesterStatus]);

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-12 animate-in fade-in duration-200">
      <div className="text-center space-y-1">
        <h2 className="text-2xl sm:text-3xl font-bold font-display text-foreground">Study Hub</h2>
        <p className="text-muted-foreground text-xs sm:text-sm max-w-lg mx-auto">
          Choose a semester to open its courses.
        </p>
        <div className="pt-2">
          <Button to="/student/quiz" variant="outline" size="sm" icon={<Brain size={14} />}>Quizzes</Button>
        </div>
      </div>

      {/* Semester Hub Cards (Semester 1, Semester 2, and fully premium semesters) */}
      {hubReady && semesterCards.some(({ status, isFullyPremium }) => {
        const unlocked = isAdminPreview || (status?.unlocked ?? false);
        const available = status ? (status.isAvailable ?? status.is_available ?? status.status !== "coming_soon") : true;
        return isFullyPremium && available && !unlocked && !status?.hasPendingRequest && !status?.has_pending_request;
      }) && (
        <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-in fade-in slide-in-from-top-1 duration-300">
          <div>
            <p className="text-sm font-bold text-foreground">Unlock your premium semesters</p>
            <p className="text-xs text-muted-foreground">Explore the available sample courses, then unlock the full course collection together.</p>
          </div>
          <button type="button" onClick={() => openUpgrade()} className="btn btn-primary px-4 py-2 text-xs font-semibold whitespace-nowrap">
            Get premium
          </button>
        </div>
      )}
      {!hubReady || subjectsLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" aria-busy="true" aria-label="Loading semesters">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-48 w-full rounded-card" />
          ))}
        </div>
      ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {semesterCards.map(({ num, title, subtitle, courses, status, badge, isFullyPremium }) => {
          const isUnlocked = isAdminPreview || (status?.unlocked ?? false);
          const isPending = Boolean(status?.hasPendingRequest || status?.has_pending_request);
          const isAvailable = status ? (status.isAvailable ?? status.is_available ?? status.status !== "coming_soon") : true;
          const isCurrent = profileSemester === num;
          const courseCount = status?.course_count ?? status?.courseCount ?? courses.length;
          const price = status?.lowest_package_price ?? status?.lowestPackagePrice ?? status?.from_price_etb;
          const expiresAt = status?.expires_at ?? status?.expiresAt;

          return (
            <div
              key={num}
              onClick={() => navigate(`/student/study/semester/${num}`)}
              className="group relative flex min-h-48 cursor-pointer flex-col justify-between overflow-hidden rounded-card border border-border bg-card p-5 shadow-soft transition-[transform,border-color] duration-200 hover:-translate-y-0.5 hover:border-primary/50 motion-reduce:transition-none motion-reduce:hover:transform-none"
            >
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-bold uppercase tracking-wider text-primary">
                        {badge}
                      </span>
                      {isFullyPremium && !isUnlocked && (
                        <span className="text-xs font-bold px-1.5 py-0.2 rounded-xs bg-warning/10 text-warning border border-warning/30">
                          Premium
                        </span>
                      )}
                      {isCurrent && (
                        <span className="text-xs font-bold px-1.5 py-0.2 rounded-xs bg-primary/10 text-primary border border-primary/30">
                          Current
                        </span>
                      )}
                    </div>
                    <h3 className="text-xl font-bold font-display text-foreground group-hover:text-primary transition-colors">
                      {title}
                    </h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{subtitle}</p>
                  </div>

                  {isUnlocked ? (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-xs bg-success/10 text-success border border-success/30 flex items-center gap-1 shrink-0">
                      <CheckCircle2 size={12} />
                      Unlocked
                    </span>
                  ) : !isAvailable ? (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-xs bg-secondary text-muted-foreground border border-border flex items-center gap-1 shrink-0">
                      Coming soon
                    </span>
                  ) : isPending ? (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-xs bg-muted text-muted-foreground border border-border flex items-center gap-1 shrink-0">
                      <Clock size={12} />
                      Awaiting review
                    </span>
                  ) : (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-xs bg-warning/10 text-warning border border-warning/30 flex items-center gap-1 shrink-0">
                      <Lock size={12} />
                      {price ? `From ${price} ETB` : "Locked"}
                    </span>
                  )}
                </div>

                <div className="space-y-1 pt-1">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <BookOpen size={13} className="text-primary" />
                    <span>{courseCount} {courseCount === 1 ? "course" : "courses"}</span>
                  </div>
                  {isUnlocked && expiresAt && (
                    <p className="text-xs text-muted-foreground">
                      Expires {new Date(expiresAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                    </p>
                  )}
                  {isPending && (
                    <p className="text-xs text-muted-foreground">
                      Payment request under review
                    </p>
                  )}
                </div>
              </div>

              <div className="pt-4 mt-3 border-t border-border/40 flex items-center justify-between">
                <ActionPill>Open courses</ActionPill>

                {isPending && (
                  <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
                    <Clock size={11} />
                    Awaiting review
                  </span>
                )}
                {!isAvailable && (
                  <span className="text-xs font-semibold text-muted-foreground">
                    Coming soon
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

/* ─── Step 2: Pick activity ─── */
export function StudyActivityPicker() {
  const navigate = useNavigate();
  const location = useLocation();
  const { subjectSlug, resolved, loading } = useResolvedStudySubject();

  // Guard against route capture: if :subject matches "semester", redirect back to Study Hub
  if (subjectSlug === "semester") {
    return <Navigate to="/student/study" replace />;
  }

  if (loading) return <StudySubjectLoading />;
  if (!subjectSlug || !resolved) {
    return <Navigate to="/student/study" replace />;
  }

  const fromSemester = (location.state as any)?.fromSemester || resolved.semester || 1;
  const backTarget = `/student/study/semester/${fromSemester}`;
  const backText = `Semester ${fromSemester}`;

  // Filter activities: if there is no note corresponding to that course hide the card
  const availableActivities = STUDY_ACTIVITIES.filter((a) => {
    if (a.id === "notes") {
      return (resolved.counts?.notes ?? 0) > 0;
    }
    return true;
  });

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div>
        <button
          type="button"
          onClick={() => location.key !== "default" ? navigate(-1) : navigate(backTarget)}
          className="inline-flex items-center gap-1.5 text-xs font-benton font-semibold text-muted-foreground hover:text-foreground mb-4 transition group"
        >
          <ChevronLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" /> Back to {backText}
        </button>
        <div className="flex items-center gap-3.5">
          {resolved.imageUrl ? (
            <img
              src={resolved.imageUrl}
              alt={resolved.name}
              className="w-14 h-14 rounded-xl object-cover border border-border shadow-xs"
            />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/20 flex items-center justify-center text-3xl">
              {resolved.emoji || "📚"}
            </div>
          )}
          <div>
            <h2 className="text-2xl font-bold">{resolved.name}</h2>
            <p className="text-muted-foreground text-sm font-mono">{resolved.code}</p>
          </div>
        </div>
      </div>

      <ChaptersEntryCard courseId={resolved.id} subjectSlug={subjectSlug} />

      {(resolved.resourceCount ?? 0) === 0 && (
        <div className="rounded-sm border border-dashed border-border bg-card/60 p-4 text-center">
          <p className="text-xs text-muted-foreground font-medium">
            No learning resources are available for this course yet.
          </p>
        </div>
      )}

      <div>
        <GlassIcons
          className="icon-btns--activity"
          items={availableActivities.map((a) => {
            const Icon = ACTIVITY_ICONS[a.icon];
            const count = resolved.counts ? (resolved.counts[a.id] ?? 0) : undefined;
            const desc = count !== undefined && count > 0
              ? `${count} available · ${a.description}`
              : a.description;
            return {
              icon: <Icon size={24} />,
              color: ACTIVITY_COLORS[a.id] ?? "blue",
              label: a.title,
              description: desc,
              actionText: a.actionText,
              onClick: () => navigate(`/student/study/${subjectSlug}/${a.id}`, { state: { fromSemester } }),
            };
          })}
        />
      </div>
    </div>
  );
}

export { StudyBreadcrumb } from "../../components/study/StudyBreadcrumb";


/* ─── Modules list (download only — no in-browser reader) ─── */
export function StudyModulesList() {
  const { openUpgrade, hasPendingRequest } = useStudent();
  const { tokens } = useAuth();
  const token = tokens?.accessToken ?? null;
  const { subjectSlug, resolved, loading: subjectLoading } = useResolvedStudySubject();
  const { moduleId } = useParams<{ moduleId?: string }>();
  const navigate = useNavigate();

  const [modules, setModules] = useState<StudentResource[]>([]);
  const [teacherResources, setTeacherResources] = useState<StudentResource[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  const courseId = resolved?.id || undefined;
  const subjectKey = resolved?.subject;

  useEffect(() => {
    if (!token || !subjectKey) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    Promise.all([
      api.student.modules.list(token, {
        subject: subjectKey,
        courseId,
        search: debouncedSearch || undefined,
        resourceType: "module",
      }),
      api.student.modules.list(token, {
        subject: subjectKey,
        courseId,
        search: debouncedSearch || undefined,
        resourceType: "teacher_resource",
      }),
    ])
      .then(([mods, teachers]) => {
        if (cancelled) return;
        setModules(mods.items);
        setTeacherResources(teachers.items);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? mapApiError(e).message : "We could not load these materials. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, subjectKey, courseId, debouncedSearch]);

  const [readingResource, setReadingResource] = useState<StudentResource | null>(null);
  const [activeTab, setActiveTab] = useState<"modules" | "handouts">("modules");

  const activeReading =
    readingResource ||
    (moduleId
      ? modules.find((m) => m.id === moduleId) || teacherResources.find((t) => t.id === moduleId)
      : null);

  if (subjectLoading) return <StudySubjectLoading />;
  if (!subjectSlug || !resolved) return <Navigate to="/student/study" replace />;

  async function downloadResource(r: StudentResource) {
    if (!token) return;
    if (r.locked) {
      if (hasPendingRequest(resolved?.semester)) return;
      openUpgrade({ semester: resolved?.semester, stream: resolved?.stream });
      return;
    }
    setDownloadingId(r.id);
    try {
      const blob = await api.student.modules.downloadBlob(token, r.id);
      saveBlobAsFile(blob, safeDownloadName(r.title, "module"));
    } catch (e) {
      setError(e instanceof ApiError ? mapApiError(e).message : "We could not download this file. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  }

  const currentList = activeTab === "modules" ? modules : teacherResources;
  const filteredList = debouncedSearch
    ? currentList.filter((r) => r.title.toLowerCase().includes(debouncedSearch.toLowerCase()))
    : currentList;

  if (activeReading) {
    return (
      <div className="max-w-7xl mx-auto space-y-4">
        <StudyBreadcrumb subjectSlug={subjectSlug} subjectName={resolved.name} semester={resolved.semester} />
        <StudentChapterReaderModal
          inline
          isOpen={true}
          onClose={() => {
            setReadingResource(null);
            navigate(`/student/study/${subjectSlug}/modules`);
          }}
          moduleId={activeReading.id}
          moduleTitle={formatDisplayTitle(activeReading.title)}
        />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <StudyBreadcrumb subjectSlug={subjectSlug} subjectName={resolved.name} semester={resolved.semester} />

      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">{resolved.name}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {modules.length} modules · {teacherResources.length} handouts
        </p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-1 bg-muted p-1 rounded-lg border border-border w-fit">
          <button
            type="button"
            onClick={() => setActiveTab("modules")}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary ${activeTab === "modules"
                ? "bg-card text-foreground border border-border"
                : "text-muted-foreground hover:text-foreground"
              }`}
          >
            Modules ({modules.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("handouts")}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary ${activeTab === "handouts"
                ? "bg-card text-foreground border border-border"
                : "text-muted-foreground hover:text-foreground"
              }`}
          >
            Handouts ({teacherResources.length})
          </button>
        </div>

        <div className="relative w-full sm:w-64">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search materials"
            aria-label="Search materials"
            className="w-full pl-9 pr-3 py-1.5 rounded-lg border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary transition"
          />
        </div>
      </div>

      {error && <p className="text-sm text-destructive mb-4">{error}</p>}
      {loading ? (
        <div className="py-20 flex justify-center text-muted-foreground">
          <Loader2 className="animate-spin" />
        </div>
      ) : filteredList.length === 0 ? (
        <div className="my-6">
          <EmptyStateCard
            title={debouncedSearch ? "No materials match your search" : activeTab === "modules" ? "No modules yet" : "No handouts yet"}
            description={debouncedSearch ? `No resources found matching "${debouncedSearch}".` : "Study resources will be published here soon."}
            action={debouncedSearch ? {
              label: "Reset search",
              onClick: () => setSearch(""),
            } : undefined}
          />
        </div>
      ) : (
        <>
        {filteredList.some((resource) => resource.locked) && !hasPendingRequest(resolved?.semester) && (
          <div className="mb-3 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-in fade-in slide-in-from-top-1 duration-300">
            <div>
              <p className="text-sm font-bold text-foreground">Unlock the course resources</p>
              <p className="text-xs text-muted-foreground">Preview the available materials, then unlock the remaining resources together.</p>
            </div>
            <button type="button" onClick={() => openUpgrade({ semester: resolved?.semester, stream: resolved?.stream })} className="btn btn-primary px-4 py-2 text-xs font-semibold whitespace-nowrap">
              Get premium
            </button>
          </div>
        )}
        <div className="border border-border divide-y divide-border rounded-lg bg-card overflow-hidden animate-in fade-in slide-in-from-bottom-1 duration-300">
          {filteredList.map((r) => {
            const busy = downloadingId === r.id;
            const isModule = activeTab === "modules";
            const displayTitle = formatDisplayTitle(r.title);

            return (
              <div
                key={r.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 hover:bg-muted/30 transition"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <FileText size={18} className="text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground truncate" title={displayTitle}>
                      {displayTitle}
                    </p>
                    {r.size && r.size !== "—" && (
                      <p className="text-xs text-muted-foreground mt-0.5">{r.size}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 self-end sm:self-auto shrink-0">
                  {isModule && (
                    r.contentPublished ? (
                      <button
                        type="button"
                        onClick={() => {
                          setReadingResource(r);
                          navigate(`/student/study/${subjectSlug}/modules/${r.id}`);
                        }}
                        className="px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10 rounded-md transition focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
                        aria-label={`Read ${displayTitle}`}
                      >
                        Read
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled
                        title="Online reading is coming soon"
                        className="px-3 py-1.5 text-xs font-semibold text-muted-foreground/60 cursor-not-allowed rounded-md border border-border/40 focus:outline-hidden"
                        aria-label="Online reading is coming soon"
                      >
                        Coming soon
                      </button>
                    )
                  )}

                  {r.locked ? (
                    hasPendingRequest(resolved?.semester) ? (
                      <span
                        title="Payment is awaiting review"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-md bg-muted text-muted-foreground border border-border cursor-default"
                      >
                        <Clock size={12} />
                        <span>Awaiting review</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-md bg-muted text-muted-foreground border border-border">
                        <Lock size={12} />
                        <span>Premium</span>
                      </span>
                    )
                  ) : (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => downloadResource(r)}
                      title="Download PDF"
                      aria-label="Download PDF"
                      className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition disabled:opacity-50 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}
    </div>
  );
}

/* ─── Quiz for subject ─── */
export function StudySubjectQuiz() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { tokens } = useAuth();
  const { isPremium, openUpgrade, hasPendingRequest } = useStudent();
  const token = tokens?.accessToken ?? null;
  const { subjectSlug, resolved, loading: subjectLoading } = useResolvedStudySubject();
  const name = resolved?.subject ?? null;
  const displayName = resolved?.name ?? "";

  const [difficulty, setDifficulty] = useState("Medium");
  const [count, setCount] = useState(isPremium ? "10" : "3");
  const [moduleId, setModuleId] = useState(searchParams.get("moduleId") || "");
  const [chapterId, setChapterId] = useState("");
  const [topicId, setTopicId] = useState("");
  const [curriculum, setCurriculum] = useState<ModuleCurriculum | null>(null);
  const [subjectCurriculum, setSubjectCurriculum] = useState<ModuleCurriculum[]>([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  const cachedQuiz = useMemo(() => (name ? getCachedSession(`quiz:${name}`) : null), [name]);

  useEffect(() => {
    const qMid = searchParams.get("moduleId");
    if (qMid) setModuleId(qMid);
  }, [searchParams]);

  useEffect(() => {
    if (!isPremium) setCount("3");
  }, [isPremium]);

  const courseId = resolved?.id || undefined;

  useEffect(() => {
    if (!token || !name) return;
    api.student.modules.curriculumBySubject(token, name, courseId)
      .then((d) => {
        const filtered = courseId
          ? d.modules.filter((m) => !m.courseId || m.courseId === courseId)
          : d.modules;
        setSubjectCurriculum(filtered);
      })
      .catch(() => setSubjectCurriculum([]));
  }, [token, name, courseId]);

  useEffect(() => {
    if (!token || !moduleId) {
      setCurriculum(null);
      return;
    }
    api.student.modules.curriculum(token, moduleId)
      .then(setCurriculum)
      .catch(() => setCurriculum(null));
  }, [token, moduleId]);

  const chapters = curriculum?.chapters ?? subjectCurriculum.flatMap((module) => module.chapters);
  const selectedChapter = chapters.find((c) => c.id === chapterId);
  const topics = selectedChapter?.topics ?? [];

  if (subjectLoading) return <StudySubjectLoading />;
  if (!subjectSlug || !resolved || !name) return <Navigate to="/student/study" replace />;

  if (resolved.counts && (resolved.counts.quiz ?? 0) === 0) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center space-y-4">
        <StudyBreadcrumb subjectSlug={subjectSlug} subjectName={displayName} activity="Quiz" semester={resolved?.semester} />
        <div className="bg-card border border-border rounded-sm p-8 space-y-3">
          <p className="text-lg font-bold text-foreground">No quizzes available</p>
          <p className="text-sm text-muted-foreground">
            No quiz questions are available for {displayName} yet.
          </p>
          <button
            type="button"
            onClick={() => location.key !== "default" ? navigate(-1) : navigate(`/student/study/${subjectSlug}`)}
            className="inline-block mt-2 text-sm font-semibold text-primary hover:underline"
          >
            ← Back to course
          </button>
        </div>
      </div>
    );
  }

  async function startQuiz() {
    if (!token || !resolved) return;
    if (!chapterId) {
      setError("Choose a chapter before starting the quiz.");
      return;
    }
    // Ensure moduleId is set: if missing, derive it from the selected chapter
    let effectiveModuleId = moduleId;
    if (!effectiveModuleId && chapterId) {
      const found = subjectCurriculum.find((m) =>
        m.chapters.some((c) => c.id === chapterId)
      );
      if (found) effectiveModuleId = found.moduleId;
    }
    if (!effectiveModuleId) {
      setError("Could not determine the module for this chapter. Please select a module first.");
      return;
    }
    setGenerating(true);
    setError("");
    try {
      const result = await api.generate.quiz(token, {
        subject: name!,
        difficulty,
        questionCount: Number(count),
        moduleId: effectiveModuleId,
        chapterId,
        topicId: topicId || undefined,
        courseId: curriculum?.courseId || resolved.id || undefined,
      });
      if (!result.questions?.length) {
        setError("There are no quiz questions for this subject yet. Please check back after more content is added.");
        return;
      }
      saveQuizSession({
        title: `${displayName} quiz`,
        questions: result.questions,
        source: "ai",
        subject: name!,
        subjectSlug,
        courseId: curriculum?.courseId || resolved.id || undefined,
        moduleId: moduleId || undefined,
        chapterId: chapterId || undefined,
        topicId: topicId || undefined,
        difficulty,
        passages: (result as any).passages || {},
      });
      navigate("/student/study/quiz/play");
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setError("There are no quiz questions for this subject yet. Please check back after more content is added.");
      } else if (e instanceof ApiError && e.status === 422) {
        setError(e instanceof ApiError ? mapApiError(e).message : "Please choose a chapter or topic from the list and try again.");
      } else {
        setError(e instanceof ApiError ? mapApiError(e).message : "We could not create this quiz. Please try again.");
      }
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="max-w-xl mx-auto">
      <StudyBreadcrumb subjectSlug={subjectSlug} subjectName={displayName} activity="Quiz" semester={resolved?.semester} />
      <div className="bg-card rounded-sm border border-border p-6 sm:p-8 space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xs bg-primary/10 text-primary border border-primary/20 flex items-center justify-center">
            <Brain size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold">{displayName} quiz</h2>
            <p className="text-sm text-muted-foreground">Practice questions from your course materials</p>
          </div>
        </div>

        {cachedQuiz?.session && (
          <div className="bg-card border border-primary/30 rounded-xs p-4 flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-bold text-primary uppercase">Saved quiz session</p>
              <p className="text-sm font-semibold truncate max-w-xs">{formatDisplayTitle(cachedQuiz.title)}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Question {(cachedQuiz.currentQuestion ?? 0) + 1} of {cachedQuiz.session.questions.length}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                saveQuizSession(cachedQuiz.session!);
                navigate("/student/study/quiz/play");
              }}
              className="px-4 py-2 bg-primary text-on-accent text-xs font-bold rounded-xs hover:brightness-105 transition flex items-center gap-1.5 shrink-0"
            >
              <Play size={14} /> Resume quiz
            </button>
          </div>
        )}

        {!isPremium && (
          <p className="text-xs text-warning bg-warning/10 border border-warning/30 rounded-lg px-3 py-2 flex items-center justify-between gap-2">
            <span>Free plan: up to 3 questions per quiz.</span>
            {hasPendingRequest(resolved?.semester) ? (
              <span className="font-semibold text-muted-foreground flex items-center gap-1">
                <Clock size={12} /> Awaiting review
              </span>
            ) : (
              <button
                type="button"
                onClick={() => openUpgrade({ semester: resolved?.semester, stream: resolved?.stream })}
                className="font-semibold underline cursor-pointer hover:text-primary transition"
              >
                Unlock Semester {resolved?.semester ?? ""}
              </button>
            )}
          </p>
        )}

        {subjectCurriculum.length > 0 && (
          <div>
            <label className="text-xs font-semibold uppercase text-muted-foreground mb-2 block">Module</label>
            <select
              value={moduleId}
              onChange={(e) => { setModuleId(e.target.value); setChapterId(""); setTopicId(""); }}
              className="w-full px-3 py-2 rounded-lg border border-border text-sm bg-card outline-none focus:border-primary"
            >
              <option value="">All modules</option>
              {subjectCurriculum.map((m) => (
                <option key={m.moduleId} value={m.moduleId}>{formatDisplayTitle(m.moduleTitle ?? m.moduleId)}</option>
              ))}
            </select>
          </div>
        )}

        {chapters.length > 0 && (
          <div>
            <label className="text-xs font-semibold uppercase text-muted-foreground mb-2 block">Chapter</label>
            <select
              value={chapterId}
              onChange={(e) => {
                const selectedId = e.target.value;
                setChapterId(selectedId);
                setTopicId("");
                // If no module is selected, auto-detect the module from the chosen chapter
                if (selectedId && !moduleId) {
                  const found = subjectCurriculum.find((m) =>
                    m.chapters.some((c) => c.id === selectedId)
                  );
                  if (found) setModuleId(found.moduleId);
                }
              }}
              className="w-full px-3 py-2 rounded-lg border border-border text-sm bg-card outline-none focus:border-primary"
            >
              <option value="">Choose a chapter</option>
              {chapters.map((c) => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          </div>
        )}

        {topics.length > 0 && (
          <div>
            <label className="text-xs font-semibold uppercase text-muted-foreground mb-2 block">Topic (optional)</label>
            <select
              value={topicId}
              onChange={(e) => setTopicId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-border text-sm bg-card outline-none focus:border-primary"
            >
              <option value="">Any topic in chapter</option>
              {topics.map((t) => (
                <option key={t.id} value={t.id}>{t.title}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="text-xs font-semibold uppercase text-muted-foreground mb-2 block">Difficulty</label>
          <div className="flex gap-2">
            {["Easy", "Medium", "Hard"].map((d) => (
              <button key={d} type="button" onClick={() => setDifficulty(d)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${difficulty === d ? "bg-primary text-on-accent" : "bg-muted"}`}>{d}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase text-muted-foreground mb-2 block">Questions</label>
          <div className="flex gap-2">
            {(isPremium ? ["5", "10", "20"] : ["3"]).map((n) => (
              <button key={n} type="button" onClick={() => setCount(n)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${count === n ? "bg-primary text-on-accent" : "bg-muted"}`}>{n}</button>
            ))}
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <Button
          type="button"
          onClick={startQuiz}
          loading={generating}
          loadingText="Building quiz…"
          variant="primary"
          size="lg"
          icon={<Sparkles size={16} />}
          className="w-full py-3.5"
        >
          Start quiz
        </Button>
      </div>
    </div>
  );
}

/* ─── Past exams for subject ─── */
const EXAM_CLASS_TABS = [
  { id: "all", label: "All" },
  { id: "final", label: "Final" },
  { id: "mid", label: "Midterm" },

] as const;

const EXAM_CLASS_LABELS: Record<string, string> = {
  final: "Final",
  mid: "Midterm",

};

export function StudySubjectExams({ embedded = false }: { embedded?: boolean } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isPremium, openUpgrade, hasPendingRequest } = useStudent();
  const { tokens } = useAuth();
  const token = tokens?.accessToken ?? null;
  const { subjectSlug, resolved, loading: subjectLoading } = useResolvedStudySubject();
  const name = resolved?.subject ?? null;
  const displayName = resolved?.name ?? "";

  const [exams, setExams] = useState<StudentExam[]>([]);
  const [classTab, setClassTab] = useState<"all" | "final" | "mid" | "coc">("all");
  const [yearFilter, setYearFilter] = useState("all");
  const [uniFilter, setUniFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown | null>(null);

  const courseId = resolved?.id || undefined;

  const fetchExams = useCallback(() => {
    if (!token || !name) return;
    setLoading(true);
    setError(null);
    api.student.exams.list(token, {
      subject: name,
      courseId,
      examClass: classTab === "all" ? undefined : classTab,
    })
      .then((data) => setExams(Array.isArray(data) ? data : []))
      .catch((err) => {
        setExams([]);
        setError(err);
      })
      .finally(() => setLoading(false));
  }, [token, name, classTab, courseId]);

  useEffect(() => {
    fetchExams();
  }, [fetchExams]);

  const years = Array.from(new Set(exams.map((e) => e.year).filter(Boolean))).sort().reverse();
  const universities = Array.from(new Set(exams.map((e) => e.university).filter((u): u is string => !!u))).sort();
  const visibleExams = exams.filter((e) => (yearFilter === "all" || e.year === yearFilter) && (uniFilter === "all" || e.university === uniFilter));

  if (subjectLoading) return <StudySubjectLoading />;
  if (!subjectSlug || !resolved || !name) return <Navigate to="/student/study" replace />;

  return (
    <div className={embedded ? "" : "max-w-4xl mx-auto"}>
      {!embedded && (
        <>
          <StudyBreadcrumb subjectSlug={subjectSlug} subjectName={displayName} activity="Past exams" semester={resolved?.semester} />
          <h1 className="text-section font-bold font-display mb-4">{displayName} past exams</h1>
        </>
      )}

      <div className="flex gap-2 mb-6 overflow-x-auto pb-2 scrollbar-none sm:flex-wrap">
        {EXAM_CLASS_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setClassTab(t.id)}
            className={`px-4 py-2 rounded-full text-xs font-semibold transition border shrink-0 ${
              classTab === t.id
                ? "bg-primary text-on-accent border-primary"
                : "bg-card border-border text-muted-foreground hover:border-primary/40"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {universities.length > 1 && (
        <label className="mb-4 flex items-center gap-2 text-sm">
          <span className="font-medium">University</span>
          <select value={uniFilter} onChange={(e) => setUniFilter(e.target.value)} className="min-h-11 rounded-control bg-soft px-3 text-base sm:text-sm">
            <option value="all">All universities</option>
            {universities.map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
        </label>
      )}
      {years.length > 1 && (
        <label className="mb-4 flex items-center gap-2 text-sm">
          <span className="font-medium">Year</span>
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            className="min-h-11 rounded-control bg-soft px-3 text-base sm:text-sm"
          >
            <option value="all">All years</option>
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
      )}

      {!isPremium && (
        <div className="mb-6 p-4 rounded-lg border border-primary/20 bg-secondary flex items-center gap-3 text-sm">
          <Lock size={16} className="text-primary" />
          <span>
            First exam in each category is free.{" "}
            <button type="button" onClick={() => openUpgrade({ semester: resolved?.semester, stream: resolved?.stream })} className="font-semibold text-primary">
              Upgrade
            </button>{" "}
            for all.
          </span>
        </div>
      )}

      {error ? (
        <div className="py-12">
          <StatusMessage
            variant="error"
            title="Something went wrong"
            message={mapApiError(error).message}
            action={{
              label: "Retry",
              onClick: fetchExams,
            }}
          />
        </div>
      ) : loading ? (
        <div className="space-y-2" aria-busy="true" aria-label="Loading past exams">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-14 w-full rounded-tile" />
          ))}
        </div>
      ) : exams.length === 0 ? (
        <div className="my-6">
          <EmptyStateCard
            icon={Award}
            title="No past exams available"
            description={
              classTab === "all"
                ? `No past exams have been published for ${displayName} yet.`
                : `No ${
                    EXAM_CLASS_LABELS[classTab]?.toLowerCase() || classTab
                  } past exams uploaded for ${displayName} yet.`
            }
            action={
              classTab !== "all"
                ? {
                    label: "View all exams",
                    onClick: () => setClassTab("all"),
                  }
                : undefined
            }
          />
        </div>
      ) : (
        <ul className="space-y-2">
          {visibleExams.map((exam) => {
            const cachedExam = getCachedSession(`exam:${exam.id}`);
            const answered = cachedExam?.answers?.filter((a) => a >= 0).length ?? 0;
            const subtitle = (
              <span className="flex flex-wrap items-center gap-1.5">
                <span>
                  {exam.questions} questions{exam.year ? ` · ${exam.year}` : ""}
                  {exam.examClass ? ` · ${EXAM_CLASS_LABELS[exam.examClass] ?? exam.examClass}` : ""}
                </span>
                {cachedExam?.session && (
                  <span className="font-semibold text-primary">
                    Question {(cachedExam.currentQuestion ?? 0) + 1} of {exam.questions} · {answered} answered
                  </span>
                )}
              </span>
            );
            if (exam.locked) {
              return (
                <li key={exam.id} className="flex flex-wrap items-center gap-2">
                  <div className="min-w-0 flex-1 basis-60">
                    <ListRow icon={<Lock size={18} />} title={formatDisplayTitle(exam.title)} subtitle="Premium" className="opacity-70" />
                  </div>
                  {hasPendingRequest(resolved?.semester) ? (
                    <span className="inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold text-muted-foreground">
                      <Clock size={14} aria-hidden="true" /> Awaiting review
                    </span>
                  ) : (
                    <Button size="sm" variant="outline" icon={<Lock size={14} />} onClick={() => openUpgrade({ semester: resolved?.semester, stream: resolved?.stream })}>
                      Unlock Semester {resolved?.semester ?? ""}
                    </Button>
                  )}
                </li>
              );
            }
            return (
              <li key={exam.id} className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <ListRow
                    to={`/student/study/${subjectSlug}/exams/${exam.id}`}
                    icon={<Trophy size={18} />}
                    title={formatDisplayTitle(exam.title)}
                    subtitle={subtitle}
                    action={<ArrowUpRight size={18} className="text-muted-foreground" aria-hidden="true" />}
                  />
                </div>
                {cachedExam?.session && (
                  <Button
                    size="sm"
                    icon={<Play size={14} />}
                    onClick={() => {
                      saveQuizSession(cachedExam.session!);
                      navigate("/student/study/quiz/play");
                    }}
                  >
                    Continue
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ─── Module short notes for subject (In-Page Book Reader) ─── */
export function StudySubjectNotes() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { subjectSlug, resolved, loading: subjectLoading } = useResolvedStudySubject();
  const name = resolved?.subject ?? null;
  const displayName = resolved?.name ?? "";
  const { tokens } = useAuth();
  const { openUpgrade, hasPendingRequest } = useStudent();
  const token = tokens?.accessToken ?? null;
  const courseId = resolved?.id || undefined;

  // Module & Curriculum State
  const [modules, setModules] = useState<StudentResource[]>([]);
  const [loadingModules, setLoadingModules] = useState(true);
  const [selectedModuleId, setSelectedModuleId] = useState<string>(searchParams.get("moduleId") || "");
  const [curriculum, setCurriculum] = useState<ModuleCurriculum | null>(null);
  const [loadingCurriculum, setLoadingCurriculum] = useState(false);

  // Topic & Reading State
  const [selectedTopicId, setSelectedTopicId] = useState<string>(searchParams.get("topicId") || "");
  const [noteData, setNoteData] = useState<TopicNoteReaderResponse | null>(null);
  const [loadingNote, setLoadingNote] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);

  // Responsive Drawer & Filter State
  const [isOutlineOpenMobile, setIsOutlineOpenMobile] = useState(false);
  const [outlineSearch, setOutlineSearch] = useState("");
  const [collapsedChapters, setCollapsedChapters] = useState<Record<string, boolean>>({});

  // 1. Fetch available modules for this course
  useEffect(() => {
    if (!token || !name) return;
    setLoadingModules(true);
    api.student.modules.list(token, {
      subject: name,
      courseId,
    })
      .then((d) => {
        const items = d.items || [];
        const pub = items.filter((m) => m.contentPublished !== false);
        const list = pub.length > 0 ? pub : items;
        setModules(list);

        const qMid = searchParams.get("moduleId");
        if (qMid && list.some((m) => m.id === qMid)) {
          setSelectedModuleId(qMid);
        } else if (list.length > 0) {
          setSelectedModuleId(list[0].id);
        }
      })
      .catch(() => setModules([]))
      .finally(() => setLoadingModules(false));
  }, [token, name, courseId]);

  // 2. Fetch curriculum whenever selectedModuleId changes
  useEffect(() => {
    if (!token || !selectedModuleId) {
      setCurriculum(null);
      return;
    }
    setLoadingCurriculum(true);
    api.student.modules.curriculum(token, selectedModuleId)
      .then((curr) => {
        setCurriculum(curr);
        const allT: string[] = [];
        (curr.chapters || []).forEach((ch) => {
          (ch.topics || []).forEach((t) => {
            allT.push(t.id);
          });
        });

        const qTid = searchParams.get("topicId");
        if (qTid && allT.includes(qTid)) {
          setSelectedTopicId(qTid);
        } else if (allT.length > 0) {
          setSelectedTopicId(allT[0]);
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("moduleId", selectedModuleId);
            next.set("topicId", allT[0]);
            return next;
          }, { replace: true });
        }
      })
      .catch(() => setCurriculum(null))
      .finally(() => setLoadingCurriculum(false));
  }, [token, selectedModuleId]);

  // 3. Fetch note content for selectedTopicId
  const loadTopicNote = useCallback(async (tId: string) => {
    if (!token || !tId) return;
    setLoadingNote(true);
    setNoteError(null);
    try {
      const res = await api.student.modules.shortNote(token, tId);
      setNoteData(res);
    } catch (err) {
      const msg = err instanceof ApiError ? mapApiError(err).message : "We could not load these notes. Please try again.";
      setNoteError(msg);
      setNoteData(null);
    } finally {
      setLoadingNote(false);
    }
  }, [token]);

  useEffect(() => {
    if (selectedTopicId) {
      loadTopicNote(selectedTopicId);
    }
  }, [selectedTopicId, loadTopicNote]);

  // Handlers
  const handleSelectTopic = (tId: string) => {
    setSelectedTopicId(tId);
    setIsOutlineOpenMobile(false);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (selectedModuleId) next.set("moduleId", selectedModuleId);
      next.set("topicId", tId);
      return next;
    }, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleModuleChange = (newModuleId: string) => {
    setSelectedModuleId(newModuleId);
    setSelectedTopicId("");
    setNoteData(null);
    setSearchParams({ moduleId: newModuleId }, { replace: true });
  };

  const toggleChapterCollapse = (chId: string) => {
    setCollapsedChapters((prev) => ({
      ...prev,
      [chId]: !prev[chId],
    }));
  };

  // Flattened topic list for global position calculation
  const allTopicsFlat = useMemo(() => {
    if (!curriculum?.chapters) return [];
    const res: Array<{
      topicId: string;
      topicTitle: string;
      chapterId: string;
      chapterTitle: string;
      chapterNumber: number;
      topicIndex: number;
    }> = [];
    let globalIdx = 0;
    curriculum.chapters.forEach((ch, chIdx) => {
      (ch.topics || []).forEach((t) => {
        globalIdx++;
        res.push({
          topicId: t.id,
          topicTitle: t.title,
          chapterId: ch.id,
          chapterTitle: ch.title,
          chapterNumber: chIdx + 1,
          topicIndex: globalIdx,
        });
      });
    });
    return res;
  }, [curriculum]);

  const currentTopicInfo = useMemo(() => {
    return allTopicsFlat.find((t) => t.topicId === selectedTopicId);
  }, [allTopicsFlat, selectedTopicId]);

  // Filtered chapters for outline search
  const filteredChapters = useMemo(() => {
    const chapters = curriculum?.chapters ?? [];
    if (!outlineSearch.trim()) return chapters;
    const q = outlineSearch.toLowerCase();
    return chapters
      .map((ch) => {
        const chapterMatches = ch.title.toLowerCase().includes(q);
        const matchingTopics = (ch.topics || []).filter((t) => t.title.toLowerCase().includes(q));
        if (chapterMatches) return ch;
        if (matchingTopics.length > 0) return { ...ch, topics: matchingTopics };
        return null;
      })
      .filter((ch): ch is NonNullable<typeof ch> => ch !== null);
  }, [curriculum, outlineSearch]);

  if (subjectLoading) return <StudySubjectLoading />;
  if (!subjectSlug || !resolved || !name) return <Navigate to="/student/study" replace />;

  // Empty state if course has no notes/modules
  if (resolved.counts && (resolved.counts.notes ?? 0) === 0 && modules.length === 0 && !loadingModules) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center space-y-4">
        <StudyBreadcrumb subjectSlug={subjectSlug} subjectName={displayName} activity="Short notes" semester={resolved?.semester} />
        <div className="text-center py-16 bg-card rounded-xl border border-dashed border-border p-8 space-y-4 shadow-xs">
          <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto">
            <Sparkles size={24} />
          </div>
          <div className="space-y-1">
            <h3 className="font-bold text-lg text-foreground">Get Premium Study Notes</h3>
            <p className="text-xs sm:text-sm text-muted-foreground max-w-md mx-auto">
              Comprehensive short notes and revision summaries for {displayName} are unlocked with Soorgaa Premium.
            </p>
          </div>
          <div className="pt-2 flex items-center justify-center gap-3">
            {hasPendingRequest(resolved?.semester) ? (
              <span className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-muted text-muted-foreground border border-border text-xs font-bold cursor-default shadow-xs">
                <Clock size={14} />
                <span>Awaiting review</span>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => openUpgrade({ semester: resolved?.semester, stream: resolved?.stream })}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-xs font-bold hover:opacity-90 transition cursor-pointer shadow-xs"
              >
                <Sparkles size={14} />
                <span>Unlock Semester {resolved?.semester ?? ""}</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => location.key !== "default" ? navigate(-1) : navigate(`/student/study/${subjectSlug}`)}
              className="px-4 py-2 rounded-lg border border-border text-xs font-semibold hover:bg-secondary transition"
            >
              ← Back to course
            </button>
          </div>
        </div>
      </div>
    );
  }

  const selectedModuleObj = modules.find((m) => m.id === selectedModuleId);

  // Outline Component (Reused in desktop sidebar and mobile drawer)
  const renderOutlineContent = () => (
    <div className="flex flex-col h-full space-y-3">
      {/* Outline Header & Search */}
      <div className="space-y-2 pb-2 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen size={16} className="text-primary" />
            <h3 className="text-sm font-bold text-foreground">Table of Contents</h3>
          </div>
          {allTopicsFlat.length > 0 && (
            <span className="text-xs font-semibold text-muted-foreground bg-secondary px-2 py-0.5 rounded-full">
              {allTopicsFlat.length} topics
            </span>
          )}
        </div>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-2.5 text-muted-foreground" />
          <input
            type="text"
            value={outlineSearch}
            onChange={(e) => setOutlineSearch(e.target.value)}
            placeholder="Search topics…"
            className="w-full pl-8 pr-2.5 py-1.5 text-xs rounded-lg border border-border bg-background focus:outline-none focus:border-primary transition"
          />
        </div>
      </div>

      {/* Chapter & Topic Sections Tree */}
      <div className="flex-1 overflow-y-auto pr-1 space-y-3 text-xs">
        {loadingCurriculum ? (
          <div className="py-6 flex items-center justify-center text-muted-foreground">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading outline…
          </div>
        ) : filteredChapters.length === 0 ? (
          <p className="py-6 text-center text-muted-foreground italic">
            {outlineSearch ? "No topics match search" : "No chapters in this module"}
          </p>
        ) : (
          filteredChapters.map((ch, chIdx) => {
            const isCollapsed = !!collapsedChapters[ch.id];
            const hasActiveTopic = (ch.topics || []).some((t) => t.id === selectedTopicId);

            return (
              <div key={ch.id} className="rounded-lg border border-border/60 bg-muted/20 overflow-hidden">
                {/* Chapter Section Bar */}
                <button
                  type="button"
                  onClick={() => toggleChapterCollapse(ch.id)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-left font-semibold transition ${hasActiveTopic ? "bg-primary/5 text-primary" : "text-foreground hover:bg-secondary/60"
                    }`}
                >
                  <div className="flex items-center gap-1.5 min-w-0 pr-2">
                    <span className="text-xs uppercase font-bold text-muted-foreground shrink-0">
                      Ch {chIdx + 1}
                    </span>
                    <span className="truncate text-xs font-semibold">{ch.title}</span>
                    {(ch as any).isLocked && (
                      <span className="inline-flex items-center gap-0.5 text-warning font-bold text-xs bg-warning/15 border border-warning/30 px-1 py-0.2 rounded shrink-0">
                        <Lock size={9} /> PRO
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0 text-muted-foreground">
                    <span className="text-xs tabular-nums">{(ch.topics || []).length}</span>
                    {isCollapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                  </div>
                </button>

                {/* Topics in Chapter */}
                {!isCollapsed && (
                  <div className="py-1 px-1.5 space-y-0.5 border-t border-border/40">
                    {(ch.topics || []).map((t, tIdx) => {
                      const isActive = t.id === selectedTopicId;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => handleSelectTopic(t.id)}
                          className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded text-left transition ${isActive
                              ? "bg-primary text-primary-foreground font-semibold shadow-xs"
                              : "text-foreground/80 hover:bg-secondary hover:text-foreground"
                            }`}
                        >
                          <div className="flex items-center gap-2 min-w-0 truncate">
                            <span
                              className={`text-xs shrink-0 tabular-nums ${isActive ? "text-primary-foreground/90 font-bold" : "text-muted-foreground"
                                }`}
                            >
                              {chIdx + 1}.{tIdx + 1}
                            </span>
                            <span className="truncate text-xs">{t.title}</span>
                          </div>
                          {(ch as any).isLocked && (
                            <Lock size={11} className={`shrink-0 ${isActive ? "text-primary-foreground/80" : "text-muted-foreground"}`} />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <div className="w-full max-w-6xl mx-auto space-y-5 px-2 sm:px-4">
      {/* Top Breadcrumb & Header Bar */}
      <StudyBreadcrumb subjectSlug={subjectSlug} subjectName={displayName} activity="Short notes" semester={resolved?.semester} />

      {/* Reader Control Bar: Module Switcher & Mobile Outline Toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-card border border-border rounded-xl p-4 shadow-xs">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary border border-primary/20 flex items-center justify-center shrink-0">
            <BookOpen size={20} className="text-primary" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl font-bold tracking-tight text-foreground">
              {displayName} Short Notes
            </h1>
            <p className="text-xs text-muted-foreground">
              {selectedModuleObj ? formatDisplayTitle(selectedModuleObj.title) : "Chapter-by-chapter reading notes"}
            </p>
          </div>
        </div>

        {/* Actions: Module Selector & Mobile Outline Button */}
        <div className="flex items-center gap-2 self-stretch sm:self-auto justify-between sm:justify-end">
          {/* Module Selector (if more than 1 module) */}
          {modules.length > 1 && (
            <div className="flex items-center gap-1.5 flex-1 sm:flex-initial">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden md:inline">
                Module:
              </span>
              <select
                value={selectedModuleId}
                onChange={(e) => handleModuleChange(e.target.value)}
                className="w-full sm:w-auto max-w-[220px] px-3 py-1.5 rounded-lg border border-border bg-secondary/50 text-xs font-medium text-foreground outline-none focus:border-primary cursor-pointer hover:bg-secondary transition"
              >
                {modules.map((m) => (
                  <option key={m.id} value={m.id}>
                    {formatDisplayTitle(m.title)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Mobile Table of Contents Toggle */}
          <button
            type="button"
            onClick={() => setIsOutlineOpenMobile(true)}
            className="lg:hidden inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary border border-primary/20 text-xs font-semibold hover:bg-primary/20 transition shrink-0"
            aria-label="Open Table of Contents"
          >
            <List size={14} />
            <span>Table of Contents</span>
            {currentTopicInfo && (
              <span className="text-xs bg-primary text-primary-foreground px-1.5 py-0.2 rounded-full tabular-nums">
                {currentTopicInfo.topicIndex}/{allTopicsFlat.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Main Responsive Book Reader Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 items-start">
        {/* Desktop Left Sidebar: Book Outline */}
        <aside className="hidden lg:block sticky top-4 max-h-[calc(100vh-5rem)] bg-card border border-border rounded-xl p-4 shadow-xs overflow-hidden">
          {renderOutlineContent()}
        </aside>

        {/* Mobile Slide-over Drawer for Outline */}
        <Backdrop
          isOpen={isOutlineOpenMobile}
          onClick={() => setIsOutlineOpenMobile(false)}
          className="lg:hidden flex"
          zIndex="z-50"
        >
          {/* Drawer Content */}
          <div
            className="relative w-[85%] max-w-sm bg-card border-r border-border h-full p-4 shadow-2xl flex flex-col z-10 animate-in slide-in-from-left duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 mb-2 border-b border-border">
              <div className="flex items-center gap-2">
                <BookOpen size={16} className="text-primary" />
                <span className="font-bold text-sm text-foreground">Chapters & Topics</span>
              </div>
              <button
                type="button"
                onClick={() => setIsOutlineOpenMobile(false)}
                className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-secondary transition cursor-pointer"
                aria-label="Close outline"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              {renderOutlineContent()}
            </div>
          </div>
        </Backdrop>

        {/* Right Column: Book Page Reader Canvas */}
        <main className="w-full bg-card border border-border rounded-xl p-4 sm:p-8 md:p-10 shadow-xs min-h-[500px]">
          {loadingCurriculum || (loadingNote && !noteData) ? (
            <ShortNotesReaderSkeleton />
          ) : noteError ? (
            <ShortNotesReaderError
              error={noteError}
              onRetry={() => selectedTopicId && loadTopicNote(selectedTopicId)}
            />
          ) : !noteData || !noteData.topic ? (
            <ShortNotesReaderEmpty onUpgrade={openUpgrade} semester={resolved?.semester} />
          ) : (
            <ShortNotesReader
              data={noteData}
              onNavigate={handleSelectTopic}
              onUpgrade={openUpgrade}
              hideBackLink={true}
              onToggleOutline={() => setIsOutlineOpenMobile(true)}
              semester={resolved?.semester}
            />
          )}
        </main>
      </div>
    </div>
  );
}

/* ─── Redirect wrapper for exam study under study flow ─── */
export function StudyExamPractice() {
  const { examId } = useParams<{ examId: string }>();
  const navigate = useNavigate();
  const { tokens } = useAuth();
  const { openUpgrade, hasPendingRequest } = useStudent();
  const token = tokens?.accessToken ?? null;
  const { subjectSlug, resolved, loading: subjectLoading } = useResolvedStudySubject();
  const name = resolved?.name ?? resolved?.subject ?? "Exam";
  const [error, setError] = useState<unknown | null>(null);
  const [emptyQuestions, setEmptyQuestions] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadExam = useCallback(async () => {
    if (!token || !examId) return;
    setLoading(true);
    setError(null);
    setEmptyQuestions(false);
    try {
      const meta = await api.student.exams.get(token, examId);
      const content = await api.student.exams.content(token, examId);
      const questions = examContentToQuiz(
        meta.title,
        content as unknown as Parameters<typeof examContentToQuiz>[1],
        name,
      );
      if (!questions.length) {
        setEmptyQuestions(true);
        return;
      }
      const warnings = Array.isArray(content.metadata?.extraction_warnings)
        ? (content.metadata.extraction_warnings as string[])
        : [];
      const partial = Boolean(content.metadata?.partial_extraction);
      saveQuizSession({
        title: meta.title,
        questions,
        source: "exam",
        examId,
        partialExtraction: partial,
        extractionWarnings: warnings,
        passages: (content as any).passages || {},
      });
      navigate("/student/study/quiz/play", { replace: true });
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [token, examId, name, navigate]);

  useEffect(() => {
    loadExam();
  }, [loadExam]);

  if (subjectLoading) return <StudySubjectLoading />;
  if (!subjectSlug || !resolved) return <Navigate to="/student/study" replace />;

  const mapped = error ? mapApiError(error) : null;

  if (mapped?.isPremiumRequired) {
    return (
      <div className="py-16 text-center max-w-md mx-auto space-y-4">
        <StudyBreadcrumb subjectSlug={subjectSlug} subjectName={name} activity="Past exams" semester={resolved?.semester} />
        <div className="p-8 bg-card rounded-sm border border-border space-y-4 shadow-xs">
          <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto">
            <Lock size={22} />
          </div>
          <h3 className="font-bold text-lg text-foreground">Premium required</h3>
          <p className="text-sm text-muted-foreground">
            This past exam is part of the premium collection. Upgrade to unlock full access.
          </p>
          <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
            {hasPendingRequest(resolved?.semester) ? (
              <span className="px-4 py-2 text-xs font-semibold rounded-xs bg-muted text-muted-foreground border border-border w-full sm:w-auto inline-flex items-center justify-center gap-1.5 cursor-default">
                <Clock size={13} />
                <span>Awaiting review</span>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => openUpgrade({ semester: resolved?.semester, stream: resolved?.stream })}
                className="btn btn-primary px-4 py-2 text-xs font-semibold rounded-xs w-full sm:w-auto"
              >
                Unlock Semester {resolved?.semester ?? ""}
              </button>
            )}
            <Link
              to={`/student/study/${subjectSlug}/exams`}
              className="text-xs font-semibold text-muted-foreground hover:text-foreground transition"
            >
              Back to past exams
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-24 flex flex-col items-center gap-3">
      {loading && (
        <>
          <Loader2 className="animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Preparing exam…</p>
        </>
      )}
      {emptyQuestions && (
        <div className="w-full max-w-md">
          <StatusMessage
            variant="info"
            title="Practice coming soon"
            message="This past exam does not have practice questions yet. Please check back later."
            action={{
              label: "Back to past exams",
              href: `/student/study/${subjectSlug}/exams`,
            }}
          />
        </div>
      )}
      {mapped && !mapped.isPremiumRequired && (
        <div className="w-full max-w-md">
          <StatusMessage
            variant={mapped.variant}
            title={mapped.title}
            message={mapped.message}
            requestId={mapped.requestId}
            action={{
              label: "Retry",
              onClick: loadExam,
            }}
          />
        </div>
      )}
    </div>
  );
}

/* ─── Quiz play under study routes (re-export path) ─── */
export { QuizPage as StudyQuizPlay, QuizResultsPage as StudyQuizResults } from "./Quiz";
